/**
 * Emergent Marketplace — Phase 1
 * VendorApprovals.jsx
 * Architectural Layer: Presentation → Admin
 *
 * Admin panel for reviewing and approving/suspending vendor applications.
 * Design: Utilitarian precision — off-white, slate, structured data grid.
 * Font: IBM Plex Serif (display) + IBM Plex Sans (body)
 */

import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, ExternalLink, Search, Filter } from "lucide-react";
import { apiFetch } from "./api";
import PractitionerCompControl from "./PractitionerCompControl";
import { Pagination } from "../../components/Pagination";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:ital,wght@0,400;0,600;1,400&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');

  .adm-root {
    --bg: #f4f3f0;
    --surface: #ffffff;
    --border: #e2dfd8;
    --border-strong: #c8c3bb;
    --slate: #2c2c2c;
    --slate-mid: #5a5a5a;
    --slate-light: #999;
    --green: #1a7a4a;
    --green-bg: #edf7f1;
    --green-border: #b2dfc5;
    --red: #c0392b;
    --red-bg: #fdf0ee;
    --red-border: #f1b8b2;
    --amber: #b7750d;
    --amber-bg: #fef9ee;
    --amber-border: #f0d28a;
    --blue: #1a5fa8;
    --blue-bg: #eef4fd;
    font-family: 'IBM Plex Sans', sans-serif;
    background: var(--bg);
    color: var(--slate);
    min-height: 100vh;
  }

  .adm-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 16px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  @media (min-width: 640px) {
    .adm-header {
      padding: 20px 32px;
    }
  }

  .adm-header-left h1 {
    font-family: 'IBM Plex Serif', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--slate);
    margin: 0 0 2px;
  }

  .adm-header-left p {
    font-size: 13px;
    color: var(--slate-light);
    margin: 0;
  }

  /* Stats row */
  .adm-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 16px 12px;
  }

  @media (min-width: 640px) {
    .adm-stats {
      gap: 12px;
      padding: 20px 32px;
    }
  }

  .adm-stat {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 10px;
  }

  @media (min-width: 640px) {
    .adm-stat {
      padding: 16px 20px;
    }
  }

  .adm-stat-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--slate-light);
    margin-bottom: 6px;
  }

  .adm-stat-value {
    font-family: 'IBM Plex Serif', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--slate);
    line-height: 1;
  }

  @media (min-width: 640px) {
    .adm-stat-label {
      font-size: 11px;
      letter-spacing: 0.1em;
    }
    .adm-stat-value {
      font-size: 28px;
    }
  }

  .adm-stat-sub {
    font-size: 11px;
    color: var(--slate-light);
    margin-top: 4px;
  }

  /* Toolbar */
  .adm-toolbar {
    display: flex;
    gap: 8px;
    padding: 0 12px 16px;
    align-items: center;
    flex-wrap: wrap;
  }

  @media (min-width: 640px) {
    .adm-toolbar {
      gap: 12px;
      padding: 0 32px 16px;
      flex-wrap: nowrap;
    }
  }

  .adm-search {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 9px 14px;
  }

  .adm-search input {
    background: none;
    border: none;
    outline: none;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px;
    color: var(--slate);
    width: 100%;
  }

  .adm-search input::placeholder { color: var(--slate-light); }

  .adm-filter-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 9px 16px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: var(--slate-mid);
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .adm-filter-btn.active {
    border-color: var(--blue);
    color: var(--blue);
    background: var(--blue-bg);
  }

  /* Tabs */
  .adm-tabs {
    display: flex;
    gap: 0;
    padding: 0 12px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (min-width: 640px) {
    .adm-tabs {
      padding: 0 32px;
    }
  }

  .adm-tab {
    padding: 10px 12px;
    white-space: nowrap;
    flex-shrink: 0;
    border: none;
    background: none;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--slate-light);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color 0.15s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .adm-tab.active {
    color: var(--slate);
    border-bottom-color: var(--slate);
  }

  .adm-tab-badge {
    background: var(--amber-bg);
    color: var(--amber);
    border: 1px solid var(--amber-border);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 600;
  }

  .adm-tab.active .adm-tab-badge {
    background: #2c2c2c;
    color: white;
    border-color: transparent;
  }

  /* Application cards */
  .adm-list {
    padding: 20px 32px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .adm-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .adm-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.06); }

  .adm-card-main {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 20px;
    cursor: pointer;
  }

  .adm-avatar {
    width: 44px; height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, #e8d4c0, #c9b89a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'IBM Plex Serif', serif;
    font-size: 18px;
    font-weight: 600;
    color: #7a5c3a;
    flex-shrink: 0;
  }

  .adm-card-info { flex: 1; min-width: 0; }

  .adm-card-name {
    font-family: 'IBM Plex Serif', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--slate);
    margin: 0 0 3px;
  }

  .adm-card-meta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
  }

  .adm-card-meta-item {
    font-size: 12px;
    color: var(--slate-light);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .adm-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .adm-status-pending  { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }
  .adm-status-approved { background: var(--green-bg); color: var(--green); border: 1px solid var(--green-border); }
  .adm-status-suspended { background: var(--red-bg); color: var(--red); border: 1px solid var(--red-border); }

  .adm-card-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
  }

  .adm-expand-btn {
    background: none;
    border: none;
    color: var(--slate-light);
    cursor: pointer;
    padding: 4px;
    display: flex;
  }

  /* Expanded detail */
  .adm-card-detail {
    border-top: 1px solid var(--border);
    padding: 20px;
    background: #fafaf8;
    animation: adm-expand 0.2s ease;
  }

  @keyframes adm-expand {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .adm-detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .adm-detail-field label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--slate-light);
    margin-bottom: 5px;
  }

  .adm-detail-field p {
    font-size: 14px;
    color: var(--slate);
    margin: 0;
    line-height: 1.55;
  }

  .adm-detail-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .adm-tag {
    padding: 3px 10px;
    background: var(--bg);
    border: 1px solid var(--border-strong);
    border-radius: 4px;
    font-size: 12px;
    color: var(--slate-mid);
  }

  /* Action buttons */
  .adm-btn-approve {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 8px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .adm-btn-approve:hover { opacity: 0.88; }

  .adm-btn-suspend {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 8px;
    border: 1px solid var(--red-border);
    background: var(--red-bg);
    color: var(--red);
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .adm-btn-suspend:hover { opacity: 0.88; }

  .adm-detail-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 16px;
    border-top: 1px solid var(--border);
    margin-top: 4px;
  }

  .adm-note-area {
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 14px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: var(--slate);
    outline: none;
    resize: vertical;
    min-height: 70px;
    box-sizing: border-box;
    margin-bottom: 12px;
  }

  .adm-note-area::placeholder { color: var(--slate-light); }
  .adm-note-area:focus { border-color: var(--slate); }

  /* Confirm modal */
  .adm-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.35);
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: adm-fadein 0.15s ease;
  }

  @keyframes adm-fadein { from { opacity: 0; } to { opacity: 1; } }

  .adm-modal {
    background: var(--surface);
    border-radius: 16px;
    padding: 28px;
    max-width: 420px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }

  .adm-modal h3 {
    font-family: 'IBM Plex Serif', serif;
    font-size: 20px;
    margin: 0 0 8px;
    color: var(--slate);
  }

  .adm-modal p {
    font-size: 14px;
    color: var(--slate-mid);
    margin: 0 0 20px;
    line-height: 1.55;
  }

  .adm-modal-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }

  .adm-btn-cancel {
    padding: 9px 18px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: none;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: var(--slate-mid);
    cursor: pointer;
  }

  .adm-empty {
    text-align: center;
    padding: 60px 32px;
    color: var(--slate-light);
  }

  .adm-empty-icon {
    width: 56px; height: 56px;
    border-radius: 50%;
    background: var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: var(--slate-light);
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const modelLabel = () => "6.5% per sale";

// ─── Vendor Card ──────────────────────────────────────────────────────────────

function VendorCard({ vendor, onApprove, onSuspend }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState(null); // { action, label, color }

  const statusIcon = {
    pending: <Clock size={11} />,
    approved: <CheckCircle size={11} />,
    suspended: <XCircle size={11} />,
  };

  return (
    <div className="adm-card">

      {/* Card main row */}
      <div className="adm-card-main" onClick={() => setExpanded(v => !v)}>
        <div className="adm-avatar">{vendor.store_name[0]}</div>
        <div className="adm-card-info">
          <p className="adm-card-name">{vendor.store_name}</p>
          <div className="adm-card-meta">
            <span className={`adm-status-pill adm-status-${vendor.status}`}>
              {statusIcon[vendor.status]} {vendor.status}
            </span>
            <span className="adm-card-meta-item">{vendor.user_name || ""}</span>
            <span className="adm-card-meta-item">{vendor.user_email || ""}</span>
            <span className="adm-card-meta-item">Applied {formatDate(vendor.created_at)}</span>
          </div>
        </div>
        <div className="adm-card-actions">
          <button className="adm-expand-btn">
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="adm-card-detail">
          <div className="adm-detail-grid">
            <div className="adm-detail-field">
              <label>Store URL</label>
              <p style={{ display: "flex", alignItems: "center", gap: 6 }}>
                /{vendor.store_slug}
                <ExternalLink size={12} color="var(--slate-light)" style={{ cursor: "pointer" }} />
              </p>
            </div>
            <div className="adm-detail-field">
              <label>Payout Model</label>
              <p>{modelLabel()}</p>
            </div>
            <div className="adm-detail-field" style={{ gridColumn: "1 / -1" }}>
              <label>Description</label>
              <p>{vendor.description}</p>
            </div>
            <div className="adm-detail-field">
              <label>Categories</label>
              <div className="adm-detail-tags">
                {(vendor.categories || []).map(c => (
                  <span key={c} className="adm-tag">{c}</span>
                ))}
              </div>
            </div>
            <div className="adm-detail-field">
              <label>Product Types</label>
              <div className="adm-detail-tags">
                {(vendor.product_types || []).map(t => (
                  <span key={t} className="adm-tag">{t}</span>
                ))}
              </div>
            </div>
            {vendor.status === "approved" && <PractitionerCompControl vendorId={vendor.id} />}
          </div>

          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--slate-light)", marginBottom: 6 }}>
              Admin Note (optional)
            </label>
            <textarea
              className="adm-note-area"
              placeholder="Internal notes visible only to admins..."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>

          <div className="adm-detail-actions">
            {vendor.status !== "suspended" && (
              <button
                className="adm-btn-suspend"
                onClick={() => setConfirm({
                  action: "suspend",
                  label: "Suspend Vendor",
                  body: `This will prevent ${vendor.store_name} from selling on the Marketplace. You can reinstate them at any time.`,
                  color: "var(--red)",
                })}
              >
                <XCircle size={14} /> Suspend
              </button>
            )}
            {vendor.status !== "approved" && (
              <button
                className="adm-btn-approve"
                onClick={() => setConfirm({
                  action: "approve",
                  label: "Approve Vendor",
                  body: `${vendor.store_name} will be notified and receive a Stripe Connect onboarding link. Their store will go live once Stripe onboarding is complete.`,
                  color: "var(--green)",
                })}
              >
                <CheckCircle size={14} /> Approve
              </button>
            )}
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <div className="adm-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <h3>{confirm.label}</h3>
            <p>{confirm.body}</p>
            <div className="adm-modal-actions">
              <button className="adm-btn-cancel" onClick={() => setConfirm(null)}>Cancel</button>
              <button
                style={{
                  padding: "9px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: confirm.color,
                  color: "white",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (confirm.action === "approve") onApprove(vendor.id);
                  if (confirm.action === "suspend") onSuspend(vendor.id);
                  setConfirm(null);
                  setExpanded(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorApprovals() {
  const [vendors, setVendors] = useState([]);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;

  useEffect(() => { setPage(1); }, [tab, search]);

  useEffect(() => {
    const load = async () => {
      try {
        const pendingResp = await apiFetch("/admin/marketplace/vendors/pending?per_page=100");
        const pending = pendingResp?.vendors || pendingResp || [];
        const approved = await apiFetch("/marketplace/vendors");
        setVendors([
          ...pending.map(v => ({ ...v, status: v.status || "pending" })),
          ...approved.map(v => ({ ...v, status: v.status || "approved" })),
        ]);
      } catch (e) {
        console.error("Failed to load vendors:", e);
      }
    };
    load();
  }, []);

  const approve = async (id) => {
    try {
      await apiFetch(`/admin/marketplace/vendors/${id}/approve`, { method: "POST" });
      setVendors(vs => vs.map(v => v.id === id ? { ...v, status: "approved" } : v));
    } catch (e) { console.error(e); }
  };

  const suspend = async (id) => {
    try {
      await apiFetch(`/admin/marketplace/vendors/${id}/suspend`, { method: "POST" });
      setVendors(vs => vs.map(v => v.id === id ? { ...v, status: "suspended" } : v));
    } catch (e) { console.error(e); }
  };

  const filtered = vendors.filter(v => {
    const matchTab = tab === "all" || v.status === tab;
    const matchSearch =
      search === "" ||
      v.store_name.toLowerCase().includes(search.toLowerCase()) ||
      v.user_email.toLowerCase().includes(search.toLowerCase()) ||
      v.user_name.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const counts = {
    pending: vendors.filter(v => v.status === "pending").length,
    approved: vendors.filter(v => v.status === "approved").length,
    suspended: vendors.filter(v => v.status === "suspended").length,
  };

  return (
    <>
      <style>{styles}</style>
      <div className="adm-root">
        <div className="adm-header">
          <div className="adm-header-left">
            <h1>Vendor Management</h1>
            <p>Review and approve vendor applications for the 11/11 Collective Connection Marketplace</p>
          </div>
        </div>

        {/* Stats */}
        <div className="adm-stats">
          <div className="adm-stat">
            <div className="adm-stat-label">Pending Review</div>
            <div className="adm-stat-value" style={{ color: "#b7750d" }}>{counts.pending}</div>
            <div className="adm-stat-sub">Awaiting admin decision</div>
          </div>
          <div className="adm-stat">
            <div className="adm-stat-label">Active Vendors</div>
            <div className="adm-stat-value" style={{ color: "#1a7a4a" }}>{counts.approved}</div>
            <div className="adm-stat-sub">Live on Marketplace</div>
          </div>
          <div className="adm-stat">
            <div className="adm-stat-label">Suspended</div>
            <div className="adm-stat-value" style={{ color: "#c0392b" }}>{counts.suspended}</div>
            <div className="adm-stat-sub">Access revoked</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="adm-toolbar">
          <div className="adm-search">
            <Search size={15} color="var(--slate-light)" />
            <input
              placeholder="Search by store name, email, or applicant..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="adm-tabs">
          {[
            { id: "pending",  label: "Pending",  badge: counts.pending },
            { id: "approved", label: "Approved", badge: null },
            { id: "suspended",label: "Suspended",badge: null },
            { id: "all",      label: "All",      badge: null },
          ].map(t => (
            <button
              key={t.id}
              className={`adm-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.badge != null && t.badge > 0 && (
                <span className="adm-tab-badge">{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="adm-list">
          {filtered.length === 0 ? (
            <div className="adm-empty">
              <div className="adm-empty-icon">
                <CheckCircle size={24} />
              </div>
              <p style={{ fontSize: 15, fontWeight: 600, color: "var(--slate-mid)", margin: "0 0 6px" }}>
                {tab === "pending" ? "No pending applications" : "No vendors found"}
              </p>
              <p style={{ fontSize: 13, margin: 0 }}>
                {tab === "pending"
                  ? "All applications have been reviewed."
                  : "Try adjusting your search or filter."}
              </p>
            </div>
          ) : (
            filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE).map(v => (
              <VendorCard
                key={v.id}
                vendor={v}
                onApprove={approve}
                onSuspend={suspend}
              />
            ))
          )}
        </div>
        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(filtered.length / PER_PAGE))}
          onChange={setPage}
          testidPrefix="vendor-approvals-pagination"
          theme="light"
        />
      </div>
    </>
  );
}
